// trigger CI: frontend change
import React, { Suspense, lazy } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { Container, CircularProgress, Box } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RoleProvider, useRole } from "./contexts/RoleContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import RequireAuth from "./components/Common/RequireAuth";
import AdminRoute from "./components/Common/AdminRoute";
import Header from "./components/Common/Header";
import AppWrapper from "./components/Common/AppWrapper";
import I18nProviderWrapper from "./components/Common/I18nProviderWrapper";
import NotificationSnackbarProvider from "./components/NotificationSnackbarProvider";
import { NotificationProvider } from "./contexts/NotificationContext";
import {
  setGlobalNavigate,
  setGlobalOpenOrderModal,
} from "./services/NotificationSnackbarService";
import OrderDetailModal from "./components/Common/OrderDetailModal";
import TermsModal, { useTermsCheck } from "./components/Common/TermsModal";
import RequirementsGate from "./components/Common/RequirementsGate";
import { preloadStatusTimeouts } from "./components/Common/OrderStatusProgress";
import { TimerProvider } from "./components/Common/TimerContext";
import LandingModern from "./pages/LandingModern";
import LandingBrigades from "./pages/fsm/LandingBrigades";
import Login from "./pages/Login";
import Register from "./pages/Register";
import YandexOAuthCallback from "./pages/YandexOAuthCallback";
import GoogleOAuthCallback from "./pages/GoogleOAuthCallback";
import { ErrorBoundary } from "./utils/errors";
import { ErrorToastContainer } from "./utils/errors";
import { ErrorOfflineBanner } from "./utils/errors";
import { useOrderModal } from "./hooks/useOrderModal";
import { orderModalToDetailProps } from "./utils/orderModalUtils";
import { getProductDomain, type ProductDomain } from "./utils/domain";

// FSM Pages
const FSMLayout = lazy(() => import("./pages/fsm/FSMLayout"));
const DispatchBoardPage = lazy(() => import("./pages/fsm/DispatchBoardPage"));
const DispatchDashboardPage = lazy(() => import("./pages/fsm/DispatchDashboardPage"));
const FSMOrderDetailPage = lazy(() => import("./pages/fsm/OrderDetailPage"));
const FSMOrderNewPage = lazy(() => import("./pages/fsm/FSMOrderNewPage"));
const WorkerManagementPage = lazy(() => import("./pages/fsm/WorkerManagementPage"));
const WorkerDetailPage = lazy(() => import("./pages/fsm/WorkerDetailPage"));
const LiveMapPage = lazy(() => import("./pages/fsm/LiveMapPage"));
const ShiftCalendarPage = lazy(() => import("./pages/fsm/ShiftCalendarPage"));
const ShiftDetailPage = lazy(() => import("./pages/fsm/ShiftDetailPage"));
const BrigadesPage = lazy(() => import("./pages/fsm/BrigadesPage"));
const BrigadeDetailPage = lazy(() => import("./pages/fsm/BrigadeDetailPage"));
const OrgMembersPage = lazy(() => import("./pages/fsm/OrgMembersPage"));
const ResourcesPage = lazy(() => import("./pages/fsm/ResourcesPage"));
const PricingPage = lazy(() => import("./pages/fsm/PricingPage"));
const InvoicesPage = lazy(() => import("./pages/fsm/InvoicesPage"));
const MessageComposerPage = lazy(() => import("./pages/fsm/MessageComposerPage"));
const InboxPage = lazy(() => import("./pages/fsm/InboxPage"));
const ReportsPage = lazy(() => import("./pages/fsm/ReportsPage"));
const PermissionsPage = lazy(() => import("./pages/fsm/PermissionsPage"));
const ChatHistoryPage = lazy(() => import("./pages/fsm/ChatHistoryPage"));

// Lazy load heavy pages
const Home = lazy(() => import("./pages/Home"));
const NewOrder = lazy(() => import("./pages/NewOrder"));
const CreateOrderWizard = lazy(() => import("./pages/CreateOrderWizard"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const OrderProcessingPage = lazy(() => import("./pages/OrderProcessingPage"));
const Admin = lazy(() => import("./pages/Admin"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileEdit = lazy(() => import("./pages/ProfileEdit"));
const Settings = lazy(() => import("./pages/Settings"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const PerformerDashboard = lazy(() => import("./pages/PerformerDashboard"));
const Help = lazy(() => import("./pages/Help"));
const HelpBrigadesPage = lazy(() => import("./pages/fsm/HelpBrigadesPage"));

// Payment pages
import PaymentSuccess from "./components/Financial/PaymentSuccess";
import PaymentFailure from "./components/Financial/PaymentFailure";

// Loading component
const PageLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="60vh"
  >
    <CircularProgress />
  </Box>
);

function RoleBasedRedirect() {
  const { currentRole, isLoading: roleLoading } = useRole();
  const { user } = useAuth();
  const domain = getProductDomain();

  if (roleLoading) {
    return <PageLoader />;
  }

  // Route by the account's primary role first: a stale selected_role_*
  // must never send an admin to the FSM board.
  const primaryRole = (user as any)?.default_role || (user as any)?.role;

  // Brigade domain: only FSM/admin routes
  if (domain === "brigade") {
    if (
      primaryRole === "admin" ||
      primaryRole === "super_admin" ||
      currentRole === "admin" ||
      currentRole === "super_admin"
    ) {
      return <Navigate to="/admin" replace />;
    }
    const fsmOrgId = localStorage.getItem("fsm_org_id");
    if (currentRole === "dispatcher" || fsmOrgId) {
      return <Navigate to="/fsm/board" replace />;
    }
    return <Navigate to="/fsm/board" replace />;
  }

  // Help domain: only client/performer routes
  return <Navigate to="/home" replace />;
}

function DomainRouteGuard({
  children,
  allowedDomains,
}: {
  children: React.ReactNode;
  allowedDomains: ProductDomain[];
}) {
  const domain = getProductDomain();
  if (!allowedDomains.includes(domain)) {
    return (
      <Navigate
        to={domain === "brigade" ? "/fsm/board" : "/home"}
        replace
      />
    );
  }
  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProviderWrapper>
        <ErrorBoundary>
          <AuthProvider>
            <RoleProvider>
              <NotificationProvider>
                <NotificationSnackbarProvider>
                  <ErrorOfflineBanner />
                  <ErrorToastContainer />
                  <TimerProvider>
                    <AppContent />
                  </TimerProvider>
                </NotificationSnackbarProvider>
              </NotificationProvider>
            </RoleProvider>
          </AuthProvider>
        </ErrorBoundary>
      </I18nProviderWrapper>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const {
    missing,
    status,
    termsOpen,
    handleTermsAccepted,
    handleTermsDecline,
    retry: retryRequirements,
  } = useTermsCheck();

  const {
    modalOrderData,
    modalOrderOpen,
    fetchAndOpenOrderModal,
    closeOrderModal,
  } = useOrderModal();

  React.useEffect(() => {
    preloadStatusTimeouts();
  }, []);

  React.useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  React.useEffect(() => {
    setGlobalOpenOrderModal(fetchAndOpenOrderModal);
  }, [fetchAndOpenOrderModal]);

  const isLandingPage = ["/", "/landing", "/for-brigades", "/landing/brigades"].includes(location.pathname);
  const isFSMRoute = location.pathname.startsWith("/fsm");
  const isLoginPage = location.pathname === "/login" || location.pathname === "/register";

  const isProtectedRoute = !["/", "/landing", "/for-brigades", "/landing/brigades", "/login", "/register"].includes(
    location.pathname,
  );

  const publicRoutes = [
    "/",
    "/landing",
    "/for-brigades",
    "/landing/brigades",
    "/login",
    "/register",
    "/oauth/yandex",
    "/oauth/google",
    "/auth/yandex/callback",
    "/auth/google/callback",
  ];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  if (isLoading && isProtectedRoute && !isPublicRoute) {
    return (
      <AppWrapper>
        <div
          className="App"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </div>
      </AppWrapper>
    );
  }

  return (
    <AppWrapper>
      <div className="App">
        {!isLandingPage && !isFSMRoute && !isLoginPage && <Header />}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Suspense fallback={<PageLoader />}>
            <TermsModal isOpen={termsOpen} onAccept={handleTermsAccepted} onClose={handleTermsDecline} />
            <RequirementsGate
              missing={missing}
              status={status}
              isPublicRoute={isPublicRoute}
              onRetry={retryRequirements}
            >
            <Routes>
              <Route
                path="/"
                element={
                  getProductDomain() === "brigade" ? (
                    <LandingBrigades />
                  ) : isAuthenticated ? (
                    <RoleBasedRedirect />
                  ) : (
                    <LandingModern />
                  )
                }
              />
              <Route
                path="/landing"
                element={
                  isAuthenticated ? (
                    <RoleBasedRedirect />
                  ) : getProductDomain() === "brigade" ? (
                    <LandingBrigades />
                  ) : (
                    <LandingModern />
                  )
                }
              />
              <Route
                path="/for-brigades"
                element={<LandingBrigades />}
              />
              <Route
                path="/landing/brigades"
                element={<LandingBrigades />}
              />
              <Route
                path="/home"
                element={
                  <RequireAuth>
                    <Home />
                  </RequireAuth>
                }
              />
              <Route
                path="/client/dashboard"
                element={
                  <RequireAuth>
                    <DomainRouteGuard allowedDomains={["help"]}>
                      <ClientDashboard />
                    </DomainRouteGuard>
                  </RequireAuth>
                }
              />
              <Route
                path="/performer/dashboard"
                element={
                  <RequireAuth>
                    <DomainRouteGuard allowedDomains={["help"]}>
                      <PerformerDashboard />
                    </DomainRouteGuard>
                  </RequireAuth>
                }
              />
              <Route
                path="/orders/new"
                element={
                  <RequireAuth>
                    <CreateOrderWizard />
                  </RequireAuth>
                }
              />
              <Route
                path="/orders/process"
                element={
                  <RequireAuth>
                    <OrderProcessingPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/orders/history"
                element={
                  <RequireAuth>
                    <div>Order History Page - Placeholder</div>
                  </RequireAuth>
                }
              />
              <Route
                path="/orders/:orderId"
                element={
                  <RequireAuth>
                    <OrderDetail />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                }
              />
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <RoleBasedRedirect />
                  ) : (
                    <Login />
                  )
                }
              />
              <Route
                path="/auth/login"
                element={
                  isAuthenticated ? (
                    <RoleBasedRedirect />
                  ) : (
                    <Login />
                  )
                }
              />
              <Route
                path="/register"
                element={
                  isAuthenticated ? (
                    <RoleBasedRedirect />
                  ) : (
                    <Register />
                  )
                }
              />
              <Route
                path="/auth/yandex/callback"
                element={<YandexOAuthCallback />}
              />
              <Route
                path="/auth/google/callback"
                element={<GoogleOAuthCallback />}
              />
              {/* FSM Routes — brigade domain only */}
              <Route element={<RequireAuth><DomainRouteGuard allowedDomains={["brigade"]}><FSMLayout /></DomainRouteGuard></RequireAuth>}>
                <Route path="/fsm/board" element={<DispatchBoardPage />} />
                <Route path="/fsm/dispatch" element={<DispatchDashboardPage />} />
                <Route path="/fsm/orders/new" element={<FSMOrderNewPage />} />
                <Route path="/fsm/orders/:orderId" element={<FSMOrderDetailPage />} />
                <Route path="/fsm/workers" element={<WorkerManagementPage />} />
                <Route path="/fsm/workers/:userId" element={<WorkerDetailPage />} />
                <Route path="/fsm/members" element={<OrgMembersPage />} />
                <Route path="/fsm/map" element={<LiveMapPage />} />
                <Route path="/fsm/calendar" element={<ShiftCalendarPage />} />
                <Route path="/fsm/shifts/:shiftId" element={<ShiftDetailPage />} />
                <Route path="/fsm/brigades" element={<BrigadesPage />} />
                <Route path="/fsm/brigades/:brigadeId" element={<BrigadeDetailPage />} />
                <Route path="/fsm/resources" element={<ResourcesPage />} />
                <Route path="/fsm/pricing" element={<PricingPage />} />
                <Route path="/fsm/invoices" element={<InvoicesPage />} />
                <Route path="/fsm/messages/compose" element={<MessageComposerPage />} />
                <Route path="/fsm/messages/inbox" element={<InboxPage />} />
                <Route path="/fsm/reports" element={<ReportsPage />} />
                <Route path="/fsm/permissions" element={<PermissionsPage />} />
                <Route path="/fsm/chats" element={<ChatHistoryPage />} />
                <Route path="/fsm/settings" element={<Settings fsm />} />
              </Route>

              <Route
                path="/profile/edit"
                element={
                  <RequireAuth>
                    <ProfileEdit />
                  </RequireAuth>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth>
                    <Profile />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <Settings />
                  </RequireAuth>
                }
              />
              <Route
                path="/help"
                element={
                  <RequireAuth>
                    <Help />
                  </RequireAuth>
                }
              />
              <Route
                path="/help/brigades"
                element={
                  <RequireAuth>
                    <HelpBrigadesPage />
                  </RequireAuth>
                }
              />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/failure" element={<PaymentFailure />} />
              <Route path="/payment/fail" element={<PaymentFailure />} />
            </Routes>
            </RequirementsGate>
          </Suspense>
        </Container>

        {modalOrderData && (
          <OrderDetailModal
            orderId={modalOrderData.id}
            open={modalOrderOpen}
            onClose={closeOrderModal}
            order={orderModalToDetailProps(modalOrderData)}
            userId={user?.id}
            onOrderCancel={closeOrderModal}
          />
        )}
      </div>
    </AppWrapper>
  );
}

export default App;
